import React, { useMemo, useState } from "react"
import { ChevronDown } from "lucide-react"

function getLocationLabel(location) {
    return (
        location?.name ??
        location?.label ??
        location?.description ??
        String(location?.id ?? "Location")
    )
}

function getLocationCompactLabel(location) {
    return (
        location?.short_code ??
        location?.weigh_in_code ??
        (location?.name ? String(location.name).slice(0, 3).toUpperCase() : "LOC")
    )
}

/**
 * @param {{
 *   value: unknown,
 *   locations: any[],
 *   collapsed: boolean,
 *   onChange: (locationId: number) => void,
 * }} props
 */
export function LocationSelector({ value, locations, collapsed, onChange }) {
    const options = useMemo(
        () =>
            (locations ?? []).map((l) => ({
                id: Number(l?.id),
                label: getLocationLabel(l),
                compactLabel: getLocationCompactLabel(l),
                raw: l,
            })),
        [locations]
    )

    const [isOpen, setIsOpen] = useState(false)

    const current = useMemo(
        () => options.find((o) => Number(o.id) === Number(value)) ?? null,
        [options, value]
    )

    if (!options.length) {
        return null
    }

    return (
        <div className="position-relative" style={{ width: collapsed ? "60px" : "12rem" }}>
            <button
                type="button"
                className={
                    "btn w-100 d-flex align-items-center " +
                    (collapsed ? "justify-content-center" : "justify-content-between text-start")
                }
                style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "white",
                }}
                aria-label="Select location"
                title={collapsed ? "Select location" : ""}
                onClick={() => setIsOpen(!isOpen)}
                disabled={options.length === 0}
            >
                <span style={{ fontSize: collapsed ? "0.75rem" : "0.875rem", fontWeight: collapsed ? 600 : 400 }}>
                    {collapsed ? (current?.compactLabel ?? String(value ?? "LOC")) : (current?.label ?? String(value ?? "Location"))}
                </span>
                {!collapsed ? <ChevronDown size={16} style={{ color: "rgba(255, 255, 255, 0.6)" }} /> : null}
            </button>

            {isOpen ? (
                <div
                    className="position-absolute w-100 mt-1 rounded shadow-lg"
                    style={{
                        backgroundColor: "#0f6170",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        zIndex: 50,
                    }}
                >
                    <ul className="list-unstyled mb-0 py-1">
                        {options.map((o) => {
                            const isSelected = Number(o.id) === Number(value)
                            return (
                                <li key={o.id}>
                                    <button
                                        type="button"
                                        className={
                                            "btn w-100 " +
                                            (collapsed ? "text-center " : "text-start ") +
                                            (isSelected ? "fw-semibold" : "")
                                        }
                                        style={{
                                            fontSize: collapsed ? "0.75rem" : "0.875rem",
                                            color: isSelected ? "white" : "rgba(255, 255, 255, 0.8)",
                                            backgroundColor: isSelected ? "rgba(255, 255, 255, 0.1)" : "transparent",
                                            border: "none",
                                            borderRadius: 0,
                                        }}
                                        onClick={() => {
                                            const next = Number(o.id)
                                            onChange(next)
                                            setIsOpen(false)
                                        }}
                                    >
                                        {collapsed ? o.compactLabel : o.label}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    )
}

